
import { computed, defineComponent, h, onMounted, reactive, ref, toRefs } from 'vue'
import { createModel } from '@/utils/component'
import { formatDate, formatThousands, toStringMaximumTimeOfDay } from '@/utils'
import { formatDraftType } from '@/utils/acceptanceBillFormatUtil'
import { useI18n } from 'vue-i18n'
import {
  batchRefuseSignForAcceptanceBill,
  batchSignForAcceptanceBill,
  getCIBApprovedPageList
} from '@/apis/accepted-bill/acceptanceReceipt'
import Container from '@/components/form/container/Container.vue'
import { ElLoading, ElMessage, ElMessageBox } from 'element-plus'
import SearchForm from '@/components/form/search/SearchForm.vue'
import SimpleTable from '@/components/table/simple/SimpleTable.vue'
import {
  AcceptedBillInfoModel,
  ApprovedPageCIBParamModel,
  BatchProcessingAcceptedBillsParamModel,
  BatchProcessingAcceptedBillsResultModel,
  ReceiveAcceptanceHistoryModel
} from '@/model/accepted-bill/acceptedBillModel'

export default defineComponent({
  name: 'IndustrialBankTab',
  components: { Container, SearchForm, SimpleTable },
  props: {
    modelValue: {
      type: Boolean,
      required: true
    },
    receiveType: {
      type: Number,
      required: true
    },
    bankCode: {
      type: String,
      required: true
    },
    bankName: {
      type: String,
      required: true
    },
    companyCode: {
      type: String,
      required: true
    },
    reqDtRange: {
      type: Array,
      required: false,
      default: () => []
    },
    draftNo: {
      type: String,
      required: false,
      default: ''
    }
  },
  emits: ['refresh', 'update:modelValue'],
  setup(props, context) {
    const { t } = useI18n()
    const tableRef = ref()
    const titleText = computed(() => {
      return props.receiveType === 1 ? t('Acceptance.batchAcceptance') : t('Acceptance.batchReject')
    })
    const confirmText = computed(() => {
      return props.receiveType === 1 ? t('Acceptance.Signature') : t('Acceptance.Refuse')
    })
    const dialogVisible = computed({
      get: () => props.modelValue,
      set: (value) => context.emit('update:modelValue', value)
    })
    const searchStruct = [
      {
        prop: 'reqDtRange',
        label: t('Acceptance.requestTime'),
        component: 'el-date-picker',
        type: 'daterange',
        model: props.reqDtRange
      },
      { prop: 'draftNo', label: t('Acceptance.draftNo'), component: 'el-input', type: 'text', model: props.draftNo }
    ]
    const acceptanceReceiptTableStruct = [
      { prop: 'id', label: t('Acceptance.id') },
      { prop: 'companyCode', label: t('company.companyCode') },
      { prop: 'requestObject', label: t('Acceptance.requestObject') },
      { prop: 'draftNo', label: t('Acceptance.draftNo'), width: 260 },
      {
        prop: 'requestDate',
        label: t('Acceptance.requestDate'),
        formatter: (row: ReceiveAcceptanceHistoryModel) => formatDate(row.requestDate, 'yyyy-MM-dd')
      },
      {
        prop: 'draftType',
        label: t('Acceptance.draftType'),
        width: 180,
        formatter: (row: ReceiveAcceptanceHistoryModel) => formatDraftType(row.draftType)
      },
      { prop: 'bankName', label: t('Acceptance.bankName'), width: 180 },
      { prop: 'draftAmt', label: t('Acceptance.draftAmt'), formatter: (row: any) => formatThousands(row.draftAmt), align: 'right' }
    ]
    const dataMap = reactive({
      searchFormStruct: searchStruct,
      searchFormModel: createModel(searchStruct) as any,
      tableData: Array<ReceiveAcceptanceHistoryModel>(),
      columnStruct: acceptanceReceiptTableStruct,
      selectedRows: Array<ReceiveAcceptanceHistoryModel>(),
      listLoading: true,
      limit: 10,
      total: 0,
      page: 1,
      selectable(row: ReceiveAcceptanceHistoryModel) {
        return row.status === 2
      },
      handleCurrentChange(page?: any) {
        dataMap.getPageList(page)
      },
      handleSizeChange(val: any) {
        dataMap.getPageList(null, val)
      },
      async getPageList(page?: any, limit?: any) {
        if (page) {
          dataMap.page = page
        }
        if (limit) {
          dataMap.limit = limit
        }

        dataMap.listLoading = true
        let reqDtStart = null as string | null
        let reqDtEnd = null as string | null
        if (dataMap.searchFormModel.reqDtRange && dataMap.searchFormModel.reqDtRange.length > 1) {
          reqDtStart = dataMap.searchFormModel.reqDtRange[0] ? formatDate(dataMap.searchFormModel.reqDtRange[0]) : null
          reqDtEnd = dataMap.searchFormModel.reqDtRange[1] ? toStringMaximumTimeOfDay(dataMap.searchFormModel.reqDtRange[1]) : null
        }

        const params: ApprovedPageCIBParamModel = {
          receiveType: props.receiveType,
          bankCode: props.bankCode,
          draftNo: dataMap.searchFormModel.draftNo,
          reqDtStart: reqDtStart,
          reqDtEnd: reqDtEnd,
          pageCurrent: dataMap.page,
          pageSize: dataMap.limit
        }
        const data = await getCIBApprovedPageList(params)
        if (data && data.data && data.data.list.length > 0) {
          dataMap.tableData = data.data.list
        } else {
          dataMap.tableData = []
          if (data?.code !== 200 && data?.msg) {
            ElMessage({
              message: data.msg,
              type: 'error',
              duration: 2000
            })
          }
        }
        dataMap.total = data?.data?.totalCount ?? 0
        tableRef.value.clearSelection()
        // Just to simulate the time of the request
        setTimeout(() => {
          dataMap.listLoading = false
        }, 0.5 * 1000)
      },
      submitSearch() {
        dataMap.page = 1
        dataMap.getPageList()
      },
      resetForm() {
        console.log('reset')
      }
    })
    const showErrorBox = (resultModel: BatchProcessingAcceptedBillsResultModel) => {
      const messages: string[] = []
      if (resultModel.numberOfSuccesses > 0) {
        messages.push(`成功: ${resultModel.numberOfSuccesses} 条`)
      }
      if (resultModel.numberOfFailures > 0) {
        messages.push(`失败: ${resultModel.numberOfFailures} 条`)
      }
      if (resultModel.resultDetail.length > 0 && !resultModel.resultDetail[0].result) {
        messages.push(resultModel.resultDetail[0].errorMgs)
      }

      ElMessageBox.alert(
        h(
          'div',
          {
            style: {
              maxHeight: '300px',
              overflow: 'auto'
            }
          },
          messages.map(msg =>
            h('div', { style: { marginBottom: '6px' } }, msg)
          )
        ) as any,
        '操作失败',
        {
          confirmButtonText: '确定',
          type: 'error'
        }
      )
    }

    const handleSuccess = () => {
      ElMessage({
        message: t('common.operateSuccess'),
        type: 'success',
        duration: 2000
      })
      dialogVisible.value = false
      context.emit('refresh')
    }
    const closeDialog = () => {
      dialogVisible.value = false
    }
    const confirmDialog = async() => {
      if (tableRef.value && tableRef.value.getSelectionRows().length > 0) {
        dataMap.selectedRows = tableRef.value.getSelectionRows()
        const billList = [] as AcceptedBillInfoModel[]
        dataMap.selectedRows.forEach((approval: ReceiveAcceptanceHistoryModel) => {
          billList.push({ id: approval.id, msgseq: approval.msgseq })
        })
        const params: BatchProcessingAcceptedBillsParamModel = {
          companyCode: props.companyCode,
          bankName: props.bankName,
          billInfoBOS: billList
        }
        let api

        if (props.receiveType === 1) {
          api = batchSignForAcceptanceBill
        } else if (props.receiveType === 2) {
          api = batchRefuseSignForAcceptanceBill
        } else {
          ElMessage({
            message: '请求类型错误',
            type: 'error',
            duration: 2000
          })
          return
        }

        const loading = ElLoading.service({
          lock: true,
          text: t('common.loading')
        })
        const data = await api(params)
        loading.close()

        if (data && data.data) {
          if (data.data.numberOfFailures > 0) {
            showErrorBox(data.data)
            await dataMap.getPageList(dataMap.page, dataMap.limit)
          } else {
            handleSuccess()
          }
        } else {
          ElMessage({
            message: data?.msg || '服务器返回错误',
            type: 'error',
            duration: 2000
          })
        }
      } else {
        const message = props.receiveType === 1 ? t('Acceptance.selectSignatureTip') : t('Acceptance.selectRefuseTip')
        ElMessage({
          message: message,
          type: 'error',
          duration: 20000
        })
      }
    }
    onMounted(() => {
      dataMap.getPageList(null, 10)
    })
    return {
      t,
      tableRef,
      titleText,
      confirmText,
      dialogVisible,
      closeDialog,
      confirmDialog,
      ...toRefs(dataMap)
    }
  }
})
